import { Component, OnInit, Inject, ElementRef, ViewEncapsulation, Input } from '@angular/core';
import { ModalService } from '../../utilities/modal/modal.service';
import { JsInterstitialService } from '../../services/js-interstitial.service';
import { RetirementService } from '../../services/retirement.service';
import { JsInterstitialInterface } from './js-interstitial.interface';
import { JsInterstitialConfig } from './js-interstitial.config';
import { LinkService } from '../../services/link.service';
import { PznService } from '../../services/pzn.service';
import { combineLatest } from 'rxjs';
import { first, filter, flatMap } from 'rxjs/operators';
import { DOCUMENT } from '@angular/common';
import { WindowRefService } from '../../services/window-ref.service';
import { SessionStorageService } from '../../services/web-storage/session-storage.service';

@Component({
  selector: 'js-interstitial',
  templateUrl: './js-interstitial.component.html',
  styleUrls: ['./js-interstitial.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class JsInterstitialComponent implements OnInit {
  @Input() firstName: string;
  @Input() lastName: string;
  props: JsInterstitialInterface = JsInterstitialConfig;

  constructor(
    private element: ElementRef,
    private _pznService: PznService,
    private _linkService: LinkService,
    private _jsInterstitialService: JsInterstitialService,
    private _retirementService: RetirementService,
    private _modalService: ModalService,
    private _window: WindowRefService,
    private _sessionStorage: SessionStorageService,
    @Inject(DOCUMENT) private document: Document,
  ) { }

  ngOnInit() {
    const interstitialViewed = this._sessionStorage.getItem('interstitialViewed', true);

    // Follows Interstitial Modal Path if No CSR User and interstitial has not been viewed already
    if (!this.document.body.classList.contains('csrview') && !interstitialViewed) {
      // Listens for observables on retirement data, permission, and link goals before making interstitial GET request
      combineLatest(this._retirementService.retirementObs, this._pznService.linkCTAObs$, this._linkService.linkEvent)
        .pipe(
          first()
        )
        .subscribe(res => {
          const retirementBalance = res[0];
          let permission = res[1].length ? 'T' : 'F';
          let goals = res[2] === 'hasGoals' ? 'T' : 'F';

          if (this._retirementService.hasRetirementBalance()) {
            this.checkInterstitial(retirementBalance, permission, goals);
          }
        })
    } else {
      this._jsInterstitialService.linkInterstitial.next(false);
    }
  }

  /*
    Currently checkInterstitial function is handling three scenarios
    It will subscribe and complete an Observable Chain for
    Job Switchers Intersitial, Link Interstitial, or Consent Interstitial
  */
  private checkInterstitial(retirementBalance, permission, goals) {

   this._jsInterstitialService
      .checkInterstitialViewed$(retirementBalance, permission, goals)
      .pipe(
        filter((notViewed: boolean) => notViewed),
        flatMap(() => this._jsInterstitialService.determineInterstitialPath())
      )
      .subscribe(interstitialWCMResponse => {
        //By default sets WCM HTML content (For Link Interstitial, Consent Interstitial, and Plan Terminated Interstitial)
        this.props.wcmContent = interstitialWCMResponse;

        if (interstitialWCMResponse.includes("jsInterstitialButton") ||
                    this._jsInterstitialService.isLowValueInterstitial()) {
          this.bindMethodsToWcmContent('#jsLowValueButton', 'click', this.clickToClose);
          this.bindMethodsToWcmContent('#jsInterstitialButton', 'click', this.clickToClose);
         } else if (this._jsInterstitialService.isLinkInterstitial()) {
          this._jsInterstitialService.linkInterstitial.next(true);

          this.bindMethodsToWcmContent('#linkInterstitialButton', 'click', this.clickToClose);
          this.bindMethodsToWcmContent('#calendar-link', 'click', this.clickToClose);

        } else if (this._jsInterstitialService.isConsentInterstitial()) {
          // Sets consentInterstitial to true and shows Consent Interstitial 
          this.props.consentInterstitial = true;
          this.props.modalSize = 'modal-lg';

          this.setFooterText();
          this.bindMethodsToWcmContent('#consentPhone', 'keyup', this.validateKeyPress);
          this.bindMethodsToWcmContent('#consentSubmit', 'click', this.submitConsent);
          this.bindMethodsToWcmContent('#dont-ask', 'click', this.clickToClose);
          this.bindMethodsToWcmContent('#continueWithoutAdding', 'click', this.clickToClose);
          this.bindMethodsToWcmContent('#profileSubmit', 'click', this.clickToClose);
        }

        this.openInterstitial();
      },
      () => {
        this._jsInterstitialService.linkInterstitial.next(false);
      });
  }

  private openInterstitial() {
    this._modalService.open(this.props.modalId);

    this._jsInterstitialService.doInterstitialAnalytics('Display');
  }

  // Method to close interstitial and make POST call to Interstitial Api (called by an Output event isHidden on Modal)
  closeInterstitial(evtType) {
    if (!this.props.consentError) {
      this._jsInterstitialService.updateInterstitial(this.props.doNotDisplay, this.props.increaseViewCount);
    }

    sessionStorage.setItem('interstitialViewed', 'true');

    this._jsInterstitialService.doInterstitialAnalytics(evtType);
  }

  // Method to indicate interstitial closed by click event (for Analytics purposes)
  private clickToClose() {
    this.props.closeEvent = 'Click';
    this._modalService.close(this.props.modalId);
  }

  // Method to bind events and functions to CONSENT and LINK interstitial elemetns fetched from WCM
  private bindMethodsToWcmContent(element, event, method) {
    setTimeout(() => {
      this.element.nativeElement.querySelector(element).addEventListener(event, method.bind(this));
    }, 0);
  }

  // Sets footer text only for CONSENT interstitial
  private setFooterText() {
    setTimeout(() => {
      this.element.nativeElement.querySelector('#footer-text').innerHTML = this._jsInterstitialService.consentText;
    }, 0);
  }

  // CONSENT interstitial Validation for keypress. If entry is not a number, hyphen, or parenthesis, show error message
  private validateKeyPress(event) {
    const consentSubmit = this.element.nativeElement.querySelector('#consentSubmit');
    
    let phoneInput = event.target.value;
    let numbers = phoneInput.replace(/\D/g, '');
    let validNumber: string;

    if (numbers.length === 10) {
      validNumber = numbers.substring(0, 3) + '-' + numbers.substring(3, 6) + '-' + numbers.substring(6);

      event.target.value = validNumber;

      consentSubmit.removeAttribute('disabled');

      this.props.phoneValid = true;

    } else {

      consentSubmit.setAttribute('disabled', true);

      this.props.phoneValid = false;
    }

    if (numbers.length >= 0 && numbers.length <= 10 && this.isValidDigit(phoneInput)) {
      this.element.nativeElement.querySelector('.phone-error').style.visibility = 'hidden';
    } else {
      this.element.nativeElement.querySelector('.phone-error').style.visibility = 'visible';
    }

  }
  // Logic to ensure character entered for phone is either digit, parenthesis, or dash
  private isValidDigit(phoneInput) {
    const phoneArray = phoneInput.split('');
    const chars = '0123456789()-';

    for (var i = 0; i < phoneArray.length; i++) {
      if (chars.indexOf(phoneArray[i]) == -1) {
        return false;
      }
    }

    return true;

  }
  /* 
    If user has phone input and phoneValid property is true, make CONSENT API call to CAPTURE consent.
    After, call Interstitial API to update interstitial and confirm that it has been viewed and
    simultaneously call Profile API to check if CONSENT phone number matches PROFILE phone.
    If it does, close interstitial immediately. If not, show HTML content letting user know there was mismatch
  */
  private submitConsent() {
    const phoneNumberInput = this.element.nativeElement.querySelector('#consentPhone').value,
          modifiedPhoneInput = phoneNumberInput.split('-').join('');

    if (phoneNumberInput && this.props.phoneValid) {
      const personalInfo = { firstName: this.firstName, lastName: this.lastName },
        phoneObj = { phone: modifiedPhoneInput },
        consentVersion = this._jsInterstitialService.consentInterstitialVersion,
        consentUrl = '/api/co/secure/consent-management/v1/consents/parties/clientconsents/PRUTCPA?channel=WEB&version=' + consentVersion + '&action=CAPTURE';

      this._jsInterstitialService.updateConsentApi(consentUrl, personalInfo, phoneObj).subscribe(() => {
        const profileUrl = '/api/secure/platform/profiles/v4/profile?sections=personalInfo,contactChannels',
          doNotDisplay = 'T',
          increaseViewCount = false;

        this.showLoadingIndicator(true);

        this._jsInterstitialService.updateInterstitial(doNotDisplay, increaseViewCount);

        this._jsInterstitialService.getUserProfile$(profileUrl)
        .subscribe(profileObj => {
          this.showLoadingIndicator(false);
          const profilePhone = profileObj.profiles[0].contactChannels.find(obj => obj.contactChannel === 'PHONE');

          if (profilePhone && (profilePhone.contactChannelValue === phoneNumberInput || profilePhone.contactChannelValue === modifiedPhoneInput)) {
            this._modalService.close(this.props.modalId);
          } else {
            this.element.nativeElement.querySelector('.phone-check').style.display = 'none';
            this.element.nativeElement.querySelector('.phone-mismatch').style.display = 'block';

            this.element.nativeElement.querySelectorAll('.pre-consent').forEach(link => {
              link.classList.add('hidden');
            });
            this.element.nativeElement.querySelectorAll('.post-consent').forEach(link => {
              link.classList.remove('hidden');
            });
            
          }
        },
        () => {
          this.props.closeEvent = 'errorClose';
          this._modalService.close(this.props.modalId);
        });
      },
      () => {
        this.props.consentError = true;
        this.props.closeEvent = 'errorClose';
        this._modalService.close(this.props.modalId);
      })
    }
  }

  //Specific to Consent Interstitial
  showLoadingIndicator(show) {
    if (show) {
      this.element.nativeElement.querySelector('.loading-indicator').style.display = 'block';
      this.element.nativeElement.querySelector('.consent-content').style.display = 'none';
    }
    else {
      this.element.nativeElement.querySelector('.loading-indicator').style.display = 'none';
      this.element.nativeElement.querySelector('.consent-content').style.display = 'block';
    }
  }

  // private declineConsent() {
  //   const consentVersion = this._jsInterstitialService.consentInterstitialVersion;
  //   const consentUrl = '/api/co/secure/consent-management/v1/consents/parties/clientconsents/LINKTCPA?channel=WEB&version=' + consentVersion + '&action=DECLINE';
  //   const personalInfo = { firstName: this.firstName, lastName: this.lastName };

  //   this._jsInterstitialService.updateConsentApi(consentUrl, personalInfo).subscribe(() => {
  //     const doNotDisplay = 'T',
  //       increaseViewCount = false;

  //     this._jsInterstitialService.updateInterstitial(doNotDisplay, increaseViewCount);
  //     this._modalService.close(this.props.modalId);
  //   },
  //     () => {
  //       this.props.consentError = true;
  //     })
  // }
}
